import {
  ArrowDown,
  CalendarDays,
  Check,
  ClipboardPlus,
  FlaskConical,
  Laptop,
  Search,
  ShoppingBag,
  Sparkles,
} from 'lucide-react';
import FrameArt from '@/components/ui/FrameArt';

/**
 * The workspace the hero's five loose windows dock into. Static markup in its
 * FINAL layout; `HeroSection` owns every motion and only offsets from here, so
 * resizing, reduced motion and no-JS all land on a real layout.
 *
 * Each window is a fragment of a different system an optical business runs —
 * a storefront, an order form, a chart, a schedule, a lab queue — and each one
 * carries a line for the same order, #4821. Loose, they are five programs.
 * Docked, they are five panels of one, and the order is lit in each in turn.
 *
 * Panels are listed in the order an order passes through them.
 *
 * `SCATTER` is indexed in DOM order of `[data-card]` and gives each window its
 * loose position as a fraction of the hero (x, y), a tilt (r), a size (s) and a
 * depth (d, 0 = far … 1 = near) that weights its parallax and how far out of
 * focus it starts. On a wide screen they cascade to the right of the copy;
 * `SCATTER_NARROW` rings the copy instead, since there it fills the width.
 */
export const SCATTER = [
  { x: 0.675, y: 0.3, r: -4, s: 0.78, d: 0.8 },
  { x: 0.885, y: 0.215, r: 3, s: 0.64, d: 0.35 },
  { x: 0.815, y: 0.53, r: 2, s: 0.88, d: 1 },
  { x: 0.645, y: 0.735, r: -3, s: 0.62, d: 0.5 },
  { x: 0.86, y: 0.82, r: 4, s: 0.56, d: 0.65 },
];
export const SCATTER_NARROW = [
  { x: 0.14, y: 0.15, r: -7, s: 0.62, d: 0.5 },
  { x: 0.86, y: 0.13, r: 6, s: 0.62, d: 0.35 },
  { x: 0.84, y: 0.9, r: -5, s: 0.62, d: 0.6 },
  { x: 0.13, y: 0.92, r: 6, s: 0.62, d: 0.4 },
  { x: 0.5, y: 0.97, r: 2, s: 0.5, d: 0.5 },
];

export const STAGES = ['Store', 'Order entry', 'Chart', 'Schedule', 'Lab'];

function Panel({ sys, title, icon: Icon, className = '', children }) {
  return (
    <div data-card className={className}>
      <div data-par className="h-full">
        <div
          data-float
          className="relative flex h-full flex-col overflow-hidden rounded-xl border border-secondary-dark/[0.08] bg-white shadow-[0_28px_70px_-28px_rgba(2,32,71,0.45)] sm:rounded-2xl"
        >
          <div className="flex items-center gap-2 border-b border-secondary-dark/[0.06] px-2.5 py-1.5 sm:px-3.5 sm:py-2.5">
            <Icon className="size-3 shrink-0 text-primary-blue sm:size-3.5" strokeWidth={2.25} />
            <span className="truncate text-[10px] font-semibold text-secondary-dark sm:text-xs">
              {title}
            </span>
            <span className="relative ml-auto hidden h-4 w-20 shrink-0 sm:block">
              {/* loose: a separate system. docked: a checked-off part of one. */}
              <span
                data-sys
                className="absolute inset-0 text-right font-mono text-[9px] uppercase leading-4 tracking-[0.12em] text-gray-400"
              >
                {sys}
              </span>
              <span
                data-card-on
                className="absolute right-0 top-0 grid size-4 place-items-center rounded-full bg-emerald-500 text-white"
              >
                <Check className="size-2.5" strokeWidth={3.5} />
              </span>
            </span>
          </div>
          <div className="flex min-h-0 flex-1 flex-col p-2 sm:p-3.5">{children}</div>
          <span
            data-hit
            className="pointer-events-none absolute inset-0 rounded-[inherit] ring-2 ring-inset ring-primary-blue"
          />
        </div>
      </div>
    </div>
  );
}

/** The line every panel has for the same order. */
function OrderLine({ children, className = '' }) {
  return (
    <div
      className={`relative flex items-center gap-2 overflow-hidden rounded-md px-1.5 py-1 sm:rounded-lg sm:px-2.5 sm:py-1.5 ${className}`}
    >
      <span data-hit-row className="absolute inset-0 bg-primary-blue/10" />
      <span className="relative font-mono text-[9px] font-semibold text-primary-blue sm:text-[11px]">
        #4821
      </span>
      <span className="relative min-w-0 flex-1 truncate text-[9px] text-secondary-dark/70 sm:text-[11px]">
        {children}
      </span>
    </div>
  );
}

const RX = [
  ['OD', '−2.25', '−0.50', '180', '+1.50'],
  ['OS', '−2.00', '−0.75', '170', '+1.50'],
];
const VISITS = [
  { t: '09:30', who: 'J. Alvarez', what: 'Eye exam' },
  { t: '10:30', who: 'E. Collins', what: 'Fitting', hit: true },
  { t: '11:15', who: 'M. Tan', what: 'Pickup' },
];
const JOBS = [
  { id: '#4821', lens: 'Progressive 1.67', station: 'Surfacing', p: 34, hit: true },
  { id: '#4819', lens: 'Single vision 1.60', station: 'Coating', p: 62 },
  { id: '#4816', lens: 'Polarized 1.59', station: 'Edging', p: 88 },
];

export default function HeroPlatform() {
  return (
    <div
      data-intro
      aria-hidden
      className="absolute inset-0 z-10 flex items-center justify-center px-3 pb-5 pt-20 sm:px-5 motion-reduce:relative motion-reduce:inset-auto motion-reduce:pb-16 motion-reduce:pt-0"
    >
      <div className="w-full max-w-[1180px]">
        <div data-board-title className="mb-4 flex items-end justify-between gap-4 px-1 sm:mb-6">
          <h2 className="display text-[clamp(28px,min(4.6vw,7vh),64px)] text-secondary-dark">
            <span className="inline-block overflow-hidden pb-[0.12em] align-bottom">
              <span data-rise className="inline-block">
                Five systems.
              </span>
            </span>{' '}
            <span className="inline-block overflow-hidden pb-[0.12em] align-bottom">
              <span data-rise className="inline-block text-primary-blue">
                One platform.
              </span>
            </span>
          </h2>
          <span className="hidden overflow-hidden md:block">
            <span data-rise className="label mb-2 flex items-center gap-2 text-gray-400">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              Optical Co. · live
            </span>
          </span>
        </div>

        <div className="relative">
          <div
            data-frame
            className="absolute -inset-2 rounded-[1.4rem] border border-white bg-white/70 shadow-[0_40px_120px_-30px_rgba(2,32,71,0.35)] backdrop-blur-xl sm:-inset-3 sm:rounded-[1.9rem]"
          />

          <div className="relative">
            {/* workspace bar: the order and the five stops it makes */}
            <div
              data-order
              className="mb-1.5 flex items-center gap-3 rounded-xl bg-ink px-2.5 py-2 text-white sm:mb-3 sm:gap-4 sm:rounded-2xl sm:px-4 sm:py-3"
            >
              <span className="flex items-center gap-2">
                <span className="grid size-6 place-items-center rounded-md bg-primary-blue">
                  <Sparkles className="size-3.5" strokeWidth={2.5} />
                </span>
                <span className="hidden text-sm font-semibold tracking-tight sm:block">
                  LenSync
                </span>
              </span>
              <span className="h-4 w-px bg-white/15" />
              <span className="font-mono text-[11px] font-semibold text-primary-blue sm:text-xs">
                #4821
              </span>
              <span className="hidden truncate text-xs text-white/70 md:block">
                Round Metal · Progressive
              </span>
              <span className="ml-auto flex items-center gap-1.5 sm:gap-3">
                {STAGES.map((s) => (
                  <span key={s} className="flex items-center gap-1.5">
                    <span className="relative h-1.5 w-5 overflow-hidden rounded-full bg-white/15 sm:w-7">
                      <span
                        data-order-dot
                        className="absolute inset-0 rounded-full bg-emerald-400"
                      />
                    </span>
                    <span className="hidden font-mono text-[9.5px] uppercase tracking-[0.1em] text-white/55 lg:block">
                      {s}
                    </span>
                  </span>
                ))}
              </span>
              <Search className="hidden size-4 text-white/40 sm:block" strokeWidth={2} />
            </div>

            <div className="grid grid-cols-2 gap-1.5 sm:gap-3 lg:grid-cols-12">
              {/* 01 — the storefront */}
              <Panel
                sys="System 01"
                title="E-Commerce"
                icon={ShoppingBag}
                className="lg:col-span-4"
              >
                <div className="flex items-center gap-2.5">
                  <span className="grid h-9 w-14 shrink-0 place-items-center rounded-md bg-gradient-to-br from-[#eef6ff] to-[#f7fbff] text-secondary-dark sm:h-14 sm:w-24 sm:rounded-lg">
                    <FrameArt shape="round" className="w-11 sm:w-[72px]" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[10px] font-semibold text-secondary-dark sm:text-[13px]">
                      Round Metal
                    </span>
                    <span className="hidden truncate text-[11px] text-gray-400 sm:block">
                      Progressive · Blue light
                    </span>
                    <span className="mt-0.5 block font-mono text-[10px] text-secondary-dark sm:text-xs">
                      $98.00
                    </span>
                  </span>
                </div>
                <svg
                  viewBox="0 0 200 36"
                  preserveAspectRatio="none"
                  className="my-2 hidden h-9 w-full sm:block"
                  fill="none"
                >
                  <path
                    d="M0 28 22 24 44 27 66 18 88 21 110 12 132 16 154 8 176 11 200 3V36H0Z"
                    fill="#01a7ff"
                    fillOpacity="0.07"
                  />
                  <path
                    d="M0 28 22 24 44 27 66 18 88 21 110 12 132 16 154 8 176 11 200 3"
                    stroke="#01a7ff"
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                <OrderLine className="mt-1.5 sm:mt-auto">Paid · checkout</OrderLine>
              </Panel>

              {/* 02 — remote order entry */}
              <Panel
                sys="System 02"
                title="Remote Order Entry"
                icon={Laptop}
                className="lg:col-span-4"
              >
                <div className="grid grid-cols-[auto_repeat(4,1fr)] gap-x-2 gap-y-1 font-mono text-[8.5px] sm:gap-x-3 sm:gap-y-1.5 sm:text-[11px]">
                  <span />
                  {['SPH', 'CYL', 'AXIS', 'ADD'].map((h) => (
                    <span
                      key={h}
                      className="text-right text-[8px] tracking-[0.08em] text-gray-400 sm:text-[9.5px]"
                    >
                      {h}
                    </span>
                  ))}
                  {RX.map((row) =>
                    row.map((v, i) => (
                      <span
                        key={row[0] + i}
                        className={
                          i ? 'text-right text-secondary-dark' : 'font-semibold text-primary-blue'
                        }
                      >
                        {v}
                      </span>
                    ))
                  )}
                </div>
                <div className="my-2 hidden flex-wrap gap-1.5 sm:flex">
                  {['PD 63', 'Progressive', '1.67', 'Blue light'].map((c) => (
                    <span
                      key={c}
                      className="rounded-md border border-secondary-dark/[0.08] px-1.5 py-0.5 font-mono text-[10px] text-secondary-dark/70"
                    >
                      {c}
                    </span>
                  ))}
                </div>
                <OrderLine className="mt-1.5 sm:mt-auto">Verified complete</OrderLine>
              </Panel>

              {/* 03 — medical record */}
              <Panel
                sys="System 03"
                title="Medical Records"
                icon={ClipboardPlus}
                className="lg:col-span-4"
              >
                <div className="flex items-center gap-2">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary-blue to-[#0077c8] text-[9px] font-bold text-white sm:size-9 sm:text-[11px]">
                    EC
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[10px] font-semibold text-secondary-dark sm:text-[13px]">
                      Emma Collins
                    </span>
                    <span className="hidden font-mono text-[10px] text-gray-400 sm:block">
                      MRN 00417
                    </span>
                  </span>
                </div>
                <div className="my-2 hidden grid-cols-3 gap-2 sm:grid">
                  {[
                    ['VA OD', '20/25'],
                    ['VA OS', '20/20'],
                    ['IOP', '15 / 16'],
                  ].map(([k, v]) => (
                    <span key={k} className="rounded-lg bg-[#f5f8fc] px-2 py-1.5">
                      <span className="block font-mono text-[8.5px] tracking-[0.08em] text-gray-400">
                        {k}
                      </span>
                      <span className="block font-mono text-[11px] text-secondary-dark">{v}</span>
                    </span>
                  ))}
                </div>
                <OrderLine className="mt-1.5 sm:mt-auto">Rx on file</OrderLine>
              </Panel>

              {/* 04 — the practice's day */}
              <Panel
                sys="System 04"
                title="Practice Management"
                icon={CalendarDays}
                className="lg:col-span-5"
              >
                <div className="space-y-1 sm:space-y-1.5">
                  {VISITS.map((v, i) => (
                    <div
                      key={v.t}
                      className={`relative flex items-center gap-2 overflow-hidden rounded-md px-1.5 py-1 sm:gap-3 sm:rounded-lg sm:px-2.5 sm:py-1.5 ${i === 2 ? 'hidden sm:flex' : ''}`}
                    >
                      {v.hit && (
                        <span data-hit-row className="absolute inset-0 bg-primary-blue/10" />
                      )}
                      <span className="relative font-mono text-[9px] text-gray-400 sm:text-[11px]">
                        {v.t}
                      </span>
                      <span
                        className={`relative h-3 w-0.5 rounded-full sm:h-4 ${v.hit ? 'bg-primary-blue' : 'bg-secondary-dark/15'}`}
                      />
                      <span className="relative min-w-0 flex-1 truncate text-[9.5px] font-medium text-secondary-dark sm:text-xs">
                        {v.who}
                      </span>
                      <span className="relative hidden text-[11px] text-gray-400 sm:block">
                        {v.what}
                      </span>
                      {v.hit && (
                        <span className="relative hidden font-mono text-[11px] font-semibold text-primary-blue sm:block">
                          #4821
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </Panel>

              {/* 05 — the lab */}
              <Panel
                sys="System 05"
                title="Lab Management"
                icon={FlaskConical}
                className="col-span-2 lg:col-span-7"
              >
                <div className="space-y-1 sm:space-y-1.5">
                  {JOBS.map((j, i) => (
                    <div
                      key={j.id}
                      className={`relative flex items-center gap-2 overflow-hidden rounded-md px-1.5 py-1 sm:gap-3 sm:rounded-lg sm:px-2.5 sm:py-1.5 ${i === 2 ? 'hidden sm:flex' : ''}`}
                    >
                      {j.hit && (
                        <span data-hit-row className="absolute inset-0 bg-primary-blue/10" />
                      )}
                      <span
                        className={`relative w-9 font-mono text-[9px] font-semibold sm:w-11 sm:text-[11px] ${j.hit ? 'text-primary-blue' : 'text-gray-400'}`}
                      >
                        {j.id}
                      </span>
                      <span className="relative w-24 truncate text-[9.5px] text-secondary-dark sm:w-36 sm:text-xs">
                        {j.lens}
                      </span>
                      <span className="relative h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-secondary-dark/[0.07]">
                        <span
                          className={`block h-full rounded-full ${j.hit ? 'bg-primary-blue' : 'bg-secondary-dark/25'}`}
                          style={{ width: `${j.p}%` }}
                        />
                      </span>
                      <span className="relative w-14 text-right font-mono text-[9px] text-secondary-dark/60 sm:w-20 sm:text-[11px]">
                        {j.station}
                      </span>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          </div>
        </div>

        <p
          data-next-hint
          className="label mt-6 flex items-center justify-center gap-2 text-gray-400 motion-reduce:hidden sm:mt-8 [@media(max-height:700px)]:hidden"
        >
          Follow order #4821 through it
          <ArrowDown className="size-3.5 text-primary-blue" strokeWidth={2.5} />
        </p>
      </div>
    </div>
  );
}
